// Admin, visão geral (croqui `screens/admin/visao-geral.tsx`, `spec/17` §8.2). Foco: **o
// número de pendências da fila**.
//
// Três leituras: `GET /admin/v1/overview` (os agregados, com `period`),
// `GET /admin/v1/system/alerts` (os críticos que entram na soma da fila) e
// `GET /admin/v1/settings/payouts-paused` (o aviso do topo). Nenhuma exige step-up -
// `ADMIN_STEP_UP_ROUTES` (`apps/api/src/admin-app.ts`) não as lista.
//
// Erro e skeleton do overview são resolvidos uma vez, no `QueueFocus`: o bloco de baixo
// vem do MESMO GET e repetir o `ErrorState` só faria a tela gritar duas vezes.

import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
  adminAlertsQueryOptions,
  adminOverviewQueryOptions,
} from '../api/queries/admin-overview.ts';
import { PageActions } from '../components/app/shell/page-slot.tsx';
import { InternationalWithdrawalsCard } from '../features/admin-overview/international-withdrawals-card.tsx';
import {
  countFiring,
  resolvePeriod,
  validateAdminOverviewSearch,
} from '../features/admin-overview/model.ts';
import { PayoutsPausedNotice } from '../features/admin-overview/payouts-paused.tsx';
import { PeriodFilter } from '../features/admin-overview/period-filter.tsx';
import { payoutsPausedQueryOptions } from '../features/admin-overview/queries.ts';
import { QueueFocus } from '../features/admin-overview/queue-focus.tsx';
import { OverviewSummary, OverviewSummarySkeleton } from '../features/admin-overview/summary.tsx';
import { RegionTabs, regionOf, regionSearchOf } from '../features/admin-region/region.tsx';

function AdminOverview() {
  const search = Route.useSearch();
  const period = resolvePeriod(search);
  const region = regionOf(search.region);
  const navigate = Route.useNavigate();
  const overview = useQuery(adminOverviewQueryOptions(period, region));
  const alerts = useQuery(adminAlertsQueryOptions());
  const payouts = useQuery(payoutsPausedQueryOptions());

  const criticalAlerts =
    alerts.data === undefined ? undefined : countFiring(alerts.data.data, 'critical');

  return (
    <>
      <PageActions>
        <RegionTabs
          value={region}
          onChange={(next) => {
            void navigate({
              search: {
                ...(search.period === undefined ? {} : { period: search.period }),
                ...regionSearchOf(next),
              },
              replace: true,
            });
          }}
        />
        <PeriodFilter
          value={period}
          onChange={(next) => {
            void navigate({
              search: { period: next, ...regionSearchOf(region) },
              replace: true,
            });
          }}
        />
      </PageActions>

      <PayoutsPausedNotice state={payouts.data} />

      <QueueFocus
        overview={overview.data}
        criticalAlerts={criticalAlerts}
        region={region}
        isPending={overview.isPending}
        error={overview.error}
        onRetry={() => {
          void overview.refetch();
        }}
      />

      {overview.isError ? null : overview.data === undefined ? (
        <OverviewSummarySkeleton />
      ) : (
        <OverviewSummary
          overview={overview.data}
          period={period}
          region={region}
          updatedAt={overview.dataUpdatedAt}
        />
      )}

      {/* WEB-135: fila de USDT pago à mão. Mesmo GET da fila do topo: sem dado, não desenha. */}
      {region === 'global' && overview.data !== undefined && (
        <InternationalWithdrawalsCard data={overview.data.international_withdrawals} />
      )}
    </>
  );
}

export const Route = createFileRoute('/admin/')({
  component: AdminOverview,
  validateSearch: validateAdminOverviewSearch,
});
